import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useI18n, fill } from "@/i18n";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { orgSchema, breadcrumb, faqSchema } from "@/lib/seo";
import { routeBySlug, ROUTES } from "@/data/routes";
import { ArrowLeft, ArrowRight, Path, Clock, CurrencyEur, Airplane, CheckCircle } from "@phosphor-icons/react";

export default function RouteDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const route = routeBySlug(slug);
  const [fare, setFare] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!route) { navigate("/strecken"); return; }
    api.post("/fare", { pickup_id: "zrh-airport", dropoff_id: route.cityId, vehicle_class: "standard", time: "10:00" })
      .then((r) => setFare(r.data)).catch(() => setFare(null));
  }, [slug]); // eslint-disable-line

  if (!route) return null;
  const name = route.name[lang] || route.name.de;
  const blurb = route.blurb[lang] || route.blurb.de;
  const price = fare ? `CHF ${fare.total.toFixed(0)}` : "…";

  const title = lang === "de"
    ? `Airport Transfer Zurich – ${name} | Taxi ab ZRH zum Festpreis`
    : `Zürich Airport Transfer to ${name} | Fixed-Fare Taxi from ZRH`;
  const desc = lang === "de"
    ? `Flughafentransfer und Taxi vom Flughafen Zürich (ZRH) nach ${name} zum Festpreis ${fare ? `ab ${price}` : ""}. Meet & Greet, 24/7, Flugüberwachung. Jetzt online buchen.`
    : `Airport transfer and taxi from Zürich Airport (ZRH) to ${name} at a fixed fare ${fare ? `from ${price}` : ""}. Meet & greet, 24/7, flight monitoring. Book online now.`;

  const faqs = [
    { q: fill(t("rd_faq1_q"), { name }), a: fare ? fill(t("rd_faq1_a"), { name, price }) : t("rd_faq1_a_nf") },
    { q: fill(t("rd_faq2_q"), { name }), a: fare ? fill(t("rd_faq2_a"), { name, eta: fare.eta_minutes, km: fare.distance_km }) : t("rd_faq2_a_nf") },
    { q: fill(t("rd_faq3_q"), { name }), a: fill(t("rd_faq3_a"), { name }) },
    { q: fill(t("rd_faq4_q"), { name }), a: fill(t("rd_faq4_a"), { name }) },
  ];

  const today = new Date().toISOString().slice(0, 10);
  const book = () => navigate("/book", { state: { service_type: "transfer", pickup_id: "zrh-airport", pickup_name: "Flughafen Zürich (ZRH)", dropoff_id: route.cityId, dropoff_name: name, date: today, time: "10:00" } });
  const benefits = [t("dben_fixed"), t("dben_meet"), t("dben_wait"), t("dben_247")];
  const related = ROUTES.filter((r) => r.slug !== route.slug).slice(0, 6);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="route-detail-page">
      <Seo title={title} description={desc} path={`/strecken/${route.slug}`}
        jsonLd={[orgSchema(),
          breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_routes"), path: "/strecken" }, { name: name, path: `/strecken/${route.slug}` }]),
          { "@context": "https://schema.org", "@type": "Service", serviceType: "Airport transfer", name: `Airport Transfer Zurich → ${name}`, provider: { "@type": "TaxiService", name: "Airport Transfer Zurich" }, areaServed: { "@type": "City", name: name }, ...(fare ? { offers: { "@type": "Offer", price: fare.total.toFixed(2), priceCurrency: "CHF" } } : {}) },
          faqSchema(faqs)]} />

      <Link to="/strecken" className="inline-flex items-center gap-2 font-body text-sm text-[#5A607A] hover:text-[#0B1533] transition-colors duration-200 mb-6"><ArrowLeft size={15} /> {t("routes_all")}</Link>
      <div className="flex items-center gap-2.5 font-body text-sm text-[#EF5A1E] font-semibold mb-3">
        <Airplane size={16} weight="fill" /> Zürich (ZRH) <ArrowRight size={13} /> {name}
      </div>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-tight">{fill(t("rd_h1"), { name })}</h1>
      <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-6 max-w-3xl">{blurb}</p>
      <p className="font-body text-base text-[#5A607A] leading-relaxed mt-3 max-w-3xl">
        {fill(t("rd_intro"), { name })}
      </p>

      <div className="grid grid-cols-3 gap-4 mt-10">
        {[
          { icon: Path, label: t("dest_distance"), val: fare ? `${fare.distance_km} km` : "…" },
          { icon: Clock, label: t("dest_travel_time"), val: fare ? `~${fare.eta_minutes} min` : "…" },
          { icon: CurrencyEur, label: t("dest_price_from"), val: price },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`route-stat-${i}`}>
            <s.icon size={20} className="text-[#EF5A1E]" />
            <p className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mt-3">{s.val}</p>
            <p className="font-body text-[10px] text-[#5A607A] uppercase tracking-wider mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-3xl bg-[#0B1533] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div className="flex items-center gap-3">
          <Airplane size={28} weight="fill" className="text-[#EF5A1E]" />
          <div>
            <p className="font-body text-xs text-white/50 uppercase tracking-wider">{t("dest_price_from")}</p>
            <p className="font-heading font-bold text-2xl text-white">{fare ? `CHF ${fare.total.toFixed(2)}` : "…"}</p>
          </div>
        </div>
        <button onClick={book} data-testid="route-book-btn" className="inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-8 py-4 hover:bg-[#D2450F] transition-colors duration-200">
          {t("dest_book_now")} <ArrowRight size={16} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-10">
        {benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl bg-[#F5F2ED] px-5 py-4">
            <CheckCircle size={20} weight="fill" className="text-[#EF5A1E] shrink-0" />
            <span className="font-body text-sm text-[#0B1533]">{b}</span>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("dfaq_h2")}</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`route-faq-${i}`}>
              <p className="font-body font-semibold text-[#0B1533]">{f.q}</p>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("routes_popular")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {related.map((r) => (
            <Link key={r.slug} to={`/strecken/${r.slug}`} className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#EF5A1E] transition-colors duration-200">
              <span className="flex items-center gap-2 font-body text-sm text-[#0B1533]"><Airplane size={15} className="text-[#EF5A1E]" /> ZRH → {(r.name[lang] || r.name.de)}</span>
              <ArrowRight size={15} className="text-[#9CA0B0] group-hover:text-[#EF5A1E] group-hover:translate-x-1 transition-all duration-200" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
